import { useEffect, useMemo, useRef, useState } from 'react';
import { api, Button, Card, ConfirmDialog, Field, InfoTip, PillButton, pillClasses, apiErrorMessage, formatMoney } from '@aralere/shared-ui';
import type {
  Business,
  Client,
  DocumentOwnerType,
  DocumentTriggerSet,
  DocumentWithLabel,
  Invoice,
  ProjectWithTotals,
  UpdateDocumentRequest,
} from '@aralere/shared-types';

// Documents admin (spec §18; Stage-3 slice 7 — s43 record §11, Q45/Q46). Documents are partitioned
// by OWNER (ownerId): a client, a project (which inherits the project's client scope), or a
// business (internal). Client access is DEFAULT-DENY — the four independent per-file triggers
// (viewable / downloadable / delivery-on-payment / watermark-before-payment) are the only grants,
// bulk-appliable together with the optional folder label. A file with ONLY delivery-on-payment is
// fully hidden from the client until its designated invoice is paid in full (Stage-4 wiring; owner
// s59). Watermarked previews apply to images + PDFs only (built in a following increment — until
// then the flag is settable and simply keeps the original locked). Deleting a SIGNED document
// requires the strong confirm (server 409 signed_confirm_required without it).

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

type ScopeKind = 'all' | 'business-admin' | 'client' | 'project';

const DOC_TYPE_SUGGESTIONS = ['agreement', 'contract', 'sow', 'proposal', 'report', 'deliverable', 'invoice-copy', 'other'];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** ISO timestamp -> MM-DD-YYYY. */
function displayDate(iso: string | null): string {
  if (!iso) return '-';
  const d = iso.slice(0, 10);
  const [y, m, dd] = d.split('-');
  return `${m}-${dd}-${y}`;
}

const OWNER_TYPE_LABEL: Record<DocumentOwnerType, string> = {
  client: 'Client',
  project: 'Project',
  'business-admin': 'Internal',
};

/** Human-readable option for the designated-invoice picker: number (or 'Draft' before issue),
 *  amount, and a paid/void marker. Value is the invoice's stable id. */
function invoiceOptionLabel(inv: Invoice): string {
  const num = inv.documentNumber ?? 'Draft (not yet issued)';
  const mark =
    inv.state === 'paid' ? ' · paid' : inv.state === 'void' ? ' · void' : inv.state === 'written-off' ? ' · written off' : '';
  return `${num} · ${formatMoney(inv.totalWithConvenienceFee)}${mark}`;
}

interface EditState {
  doc: DocumentWithLabel;
  title: string;
  docType: string;
  folder: string;
  issuedDate: string;
  signed: boolean;
  signedDate: string;
  agreedUpon: boolean;
  clientViewable: boolean;
  clientDownloadable: boolean;
  deliveryOnPayment: boolean;
  deliveryInvoiceId: string;
  watermarkBeforePayment: boolean;
}

function editFromDoc(doc: DocumentWithLabel): EditState {
  return {
    doc,
    title: doc.title,
    docType: doc.docType,
    folder: doc.folder ?? '',
    issuedDate: doc.issuedDate ?? '',
    signed: doc.signed,
    signedDate: doc.signedDate ?? '',
    agreedUpon: doc.agreedUpon,
    clientViewable: doc.clientViewable,
    clientDownloadable: doc.clientDownloadable,
    deliveryOnPayment: doc.deliveryOnPayment,
    deliveryInvoiceId: doc.deliveryInvoiceId ?? '',
    watermarkBeforePayment: doc.watermarkBeforePayment,
  };
}

type TriChoice = '' | 'on' | 'off';

interface BulkState {
  clientViewable: TriChoice;
  clientDownloadable: TriChoice;
  deliveryOnPayment: TriChoice;
  watermarkBeforePayment: TriChoice;
  folderMode: '' | 'set' | 'clear';
  folder: string;
  /** Slice 4: bulk-link the selection to one invoice ('set' + invoiceId) or clear the links. */
  invoiceMode: '' | 'set' | 'clear';
  invoiceId: string;
}

const emptyBulk = (): BulkState => ({
  clientViewable: '',
  clientDownloadable: '',
  deliveryOnPayment: '',
  watermarkBeforePayment: '',
  folderMode: '',
  folder: '',
  invoiceMode: '',
  invoiceId: '',
});

export function AdminDocuments(): JSX.Element {
  const [clients, setClients] = useState<Client[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [projects, setProjects] = useState<ProjectWithTotals[]>([]);

  const [scopeKind, setScopeKind] = useState<ScopeKind>('all');
  const [businessId, setBusinessId] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');

  const [docs, setDocs] = useState<DocumentWithLabel[]>([]);
  const [folderFilter, setFolderFilter] = useState<string>(''); // '' = all, '__none__' = no folder
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadView, setUploadView] = useState(false);
  const [uploadDownload, setUploadDownload] = useState(false);
  const [uploadOnPayment, setUploadOnPayment] = useState(false);
  const [uploadWatermark, setUploadWatermark] = useState(false);
  const [uploadInvoiceId, setUploadInvoiceId] = useState('');
  const [uploadInvoices, setUploadInvoices] = useState<Invoice[] | null>(null); // the current client scope's invoices
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<BulkState>(emptyBulk());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Slice 4: every invoice (id -> number/state, for the per-row linked-invoice badge) + the
  // all-active project -> client map (to resolve a selection's single client for bulk linking).
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [projectClients, setProjectClients] = useState<Map<string, string>>(new Map());

  const [editing, setEditing] = useState<EditState | null>(null);
  const [editInvoices, setEditInvoices] = useState<Invoice[] | null>(null); // the editing doc's client's invoices (null = loading / n-a)
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DocumentWithLabel | null>(null);
  const [deleting, setDeleting] = useState(false);

  // The concrete owner scope the picker currently resolves to (null = the all view).
  const scope = useMemo((): { ownerType: DocumentOwnerType; ownerId: string } | null => {
    if (scopeKind === 'business-admin' && businessId) return { ownerType: 'business-admin', ownerId: businessId };
    if (scopeKind === 'client' && clientId) return { ownerType: 'client', ownerId: clientId };
    if (scopeKind === 'project' && projectId) return { ownerType: 'project', ownerId: projectId };
    return null;
  }, [scopeKind, businessId, clientId, projectId]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, b] = await Promise.all([api.admin.listClients(), api.admin.listBusinesses()]);
        if (!alive) return;
        setClients(c.clients.map((r) => r.client)); // listClients returns enriched rows { client, ... }
        setBusinesses(b.businesses);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Slice 4 (best-effort — a failure only costs the badges/bulk-link, never the page): all
  // invoices for the id -> number badge map, and the all-active project -> client map.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [inv, proj] = await Promise.all([api.admin.listInvoices(), api.admin.listProjects()]);
        if (!alive) return;
        setAllInvoices(inv.invoices);
        setProjectClients(new Map(proj.projects.map((pr) => [pr.id, pr.clientId])));
      } catch {
        /* badges/bulk-link degrade gracefully */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const invoiceById = useMemo(() => new Map(allInvoices.map((i) => [i.id, i])), [allInvoices]);

  /** A document's client: client docs own it directly; project docs resolve via the active-project
   *  map; internal docs have none. */
  const docClientId = (d: DocumentWithLabel): string | null =>
    d.ownerType === 'client' ? d.ownerId : d.ownerType === 'project' ? projectClients.get(d.ownerId) ?? null : null;


  // Project scope needs the picked client's projects.
  useEffect(() => {
    if (scopeKind !== 'project' || !clientId) {
      setProjects([]);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const r = await api.admin.listProjects(clientId);
        if (alive) setProjects(r.projects);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [scopeKind, clientId]);

  // The current client scope's invoices, for linking an upload's deliver-on-payment / watermark
  // trigger to the invoice that unlocks it (client + project scopes carry a client via clientId).
  useEffect(() => {
    if (!clientId) {
      setUploadInvoices(null);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const r = await api.admin.listInvoices(clientId);
        if (alive) setUploadInvoices(r.invoices);
      } catch {
        if (alive) setUploadInvoices([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [clientId]);

  async function loadDocs(): Promise<void> {
    setListLoading(true);
    setError(null);
    try {
      const r = await api.admin.listDocuments(scope ?? undefined);
      setDocs(r.documents);
      setSelected(new Set());
      setFolderFilter('');
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setListLoading(false);
    }
  }

  // The live folder inventory: a folder exists exactly while at least one document carries it.
  const folders = useMemo<string[]>(() => {
    const s = new Set<string>();
    for (const d of docs) if (d.folder) s.add(d.folder);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [docs]);
  const visibleDocs = useMemo(() => {
    if (!folderFilter) return docs;
    if (folderFilter === '__none__') return docs.filter((d) => !d.folder);
    return docs.filter((d) => d.folder === folderFilter);
  }, [docs, folderFilter]);

  /** The ONE client the whole selection resolves to, or null (mixed/unresolvable) — an invoice
   *  belongs to one client, so bulk-linking is offered only then. */
  /** Light-blue attention on the two protection selects once an invoice is chosen (owner rule). */
  const bulkInvoiceAttention = bulk.invoiceMode === 'set' && bulk.invoiceId ? ' ring-2 ring-[var(--tint-blue-border)] bg-[var(--tint-blue-bg-soft)]' : '';

  const bulkClientId = useMemo((): string | null => {
    const ids = visibleDocs.filter((d) => selected.has(d.id)).map(docClientId);
    if (ids.length === 0 || ids.some((c) => c === null)) return null;
    const first = ids[0];
    return ids.every((c) => c === first) ? first : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, visibleDocs, projectClients]);

  useEffect(() => {
    // 'all' is always loadable; a concrete kind waits until its picker resolves.
    if (scopeKind !== 'all' && !scope) {
      setDocs([]);
      setSelected(new Set());
      return;
    }
    void loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scope?.ownerType, scope?.ownerId]);

  // Resolve the editing document's client and load its invoices for the designated-invoice picker.
  // Client docs: ownerId IS the client. Project docs: resolve via the all-active projects overview.
  // Internal (business-admin) docs are never client-delivered, so they get no picker (null).
  useEffect(() => {
    if (!editing || editing.doc.ownerType === 'business-admin') {
      setEditInvoices(null);
      return;
    }
    const doc = editing.doc;
    let alive = true;
    setEditInvoices(null); // show "loading" until resolved
    void (async () => {
      let cid: string | null = doc.ownerType === 'client' ? doc.ownerId : null;
      if (doc.ownerType === 'project') {
        try {
          const r = await api.admin.listProjects();
          cid = r.projects.find((p) => p.id === doc.ownerId)?.clientId ?? null;
        } catch {
          cid = null;
        }
      }
      if (!alive) return;
      if (!cid) {
        setEditInvoices([]);
        return;
      }
      try {
        const r = await api.admin.listInvoices(cid);
        if (alive) setEditInvoices(r.invoices);
      } catch {
        if (alive) setEditInvoices([]);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.doc.id]);

  async function doUpload(files: FileList | null): Promise<void> {
    if (!files || files.length === 0 || !scope) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await api.admin.uploadDocument(scope, file, {
          docType: uploadDocType.trim() || undefined,
          folder: uploadFolder.trim() || undefined,
          clientViewable: uploadView,
          clientDownloadable: uploadDownload,
          deliveryOnPayment: uploadOnPayment,
          watermarkBeforePayment: uploadWatermark || (uploadOnPayment && (uploadView || uploadDownload)),
          deliveryInvoiceId: uploadInvoiceId || undefined,
        });
      }
      setUploadView(false);
      setUploadDownload(false);
      setUploadOnPayment(false);
      setUploadWatermark(false);
      setUploadInvoiceId('');
      await loadDocs();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  // The forced-watermark invariant, surfaced in the UPLOAD form: on-payment + (view|download)
  // forces watermark on. (At upload we can't know each file's type, so we force the flag for the
  // batch; the server no-ops it for non-previewable types, which are hard-locked instead.)
  const uploadWatermarkForced = uploadOnPayment && (uploadView || uploadDownload);

  function toggleSelected(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** The pending bulk relink confirmation (s72 override guard): docs whose EXISTING invoice link
   *  differs from the chosen one, held until the admin confirms the overwrite. */
  const [bulkConfirm, setBulkConfirm] = useState<{
    set: DocumentTriggerSet;
    items: { id: string; ownerId: string }[];
    conflicts: { title: string; currentLabel: string }[];
  } | null>(null);

  async function applyBulk(): Promise<void> {
    const set: DocumentTriggerSet = {};
    if (bulk.clientViewable) set.clientViewable = bulk.clientViewable === 'on';
    if (bulk.clientDownloadable) set.clientDownloadable = bulk.clientDownloadable === 'on';
    if (bulk.deliveryOnPayment) set.deliveryOnPayment = bulk.deliveryOnPayment === 'on';
    if (bulk.watermarkBeforePayment) set.watermarkBeforePayment = bulk.watermarkBeforePayment === 'on';
    if (bulk.folderMode === 'set') {
      if (!bulk.folder.trim()) {
        setError('Enter a folder name to set (or choose Clear folder).');
        return;
      }
      set.folder = bulk.folder.trim();
    }
    if (bulk.folderMode === 'clear') set.folder = null;
    if (bulk.invoiceMode === 'set') {
      if (!bulkClientId) {
        setError('Bulk invoice linking needs a selection that all belongs to one client.');
        return;
      }
      if (!bulk.invoiceId) {
        setError('Pick the invoice to link (or choose Clear link).');
        return;
      }
      set.deliveryInvoiceId = bulk.invoiceId;
    }
    if (bulk.invoiceMode === 'clear') set.deliveryInvoiceId = null;
    if (Object.keys(set).length === 0) {
      setError('Pick at least one change to apply.');
      return;
    }
    const selectedDocs = visibleDocs.filter((d) => selected.has(d.id));
    const items = selectedDocs.map((d) => ({ id: d.id, ownerId: d.ownerId }));
    if (items.length === 0) return;

    // Override guard (s72): linking to an invoice must never SILENTLY overwrite a different
    // existing link — surface the affected docs and confirm before applying. Clearing and
    // re-applying the same invoice pass straight through.
    if (typeof set.deliveryInvoiceId === 'string') {
      const target = set.deliveryInvoiceId;
      const conflicts = selectedDocs
        .filter((d) => d.deliveryInvoiceId && d.deliveryInvoiceId !== target)
        .map((d) => ({
          title: d.title,
          currentLabel: invoiceById.get(d.deliveryInvoiceId as string)?.documentNumber ?? 'a draft invoice',
        }));
      if (conflicts.length > 0) {
        setBulkConfirm({ set, items, conflicts });
        return;
      }
    }
    await submitBulk(set, items, false);
  }

  async function submitBulk(set: DocumentTriggerSet, items: { id: string; ownerId: string }[], override: boolean): Promise<void> {
    setBulkBusy(true);
    setError(null);
    try {
      await api.admin.bulkUpdateDocuments({ items, set }, override);
      setBulk(emptyBulk());
      setBulkConfirm(null);
      await loadDocs();
    } catch (e) {
      setError(apiErrorMessage(e));
      setBulkConfirm(null);
    } finally {
      setBulkBusy(false);
    }
  }

  async function doSave(): Promise<void> {
    if (!editing) return;
    setFormError(null);
    if (!editing.title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!editing.docType.trim()) {
      setFormError('Document type is required.');
      return;
    }
    const body: UpdateDocumentRequest = {
      title: editing.title.trim(),
      docType: editing.docType.trim(),
      folder: editing.folder.trim() ? editing.folder.trim() : null,
      issuedDate: editing.issuedDate || null,
      signed: editing.signed,
      signedDate: editing.signedDate || null,
      agreedUpon: editing.agreedUpon,
      clientViewable: editing.clientViewable,
      clientDownloadable: editing.clientDownloadable,
      deliveryOnPayment: editing.deliveryOnPayment,
      deliveryInvoiceId: editing.deliveryInvoiceId.trim() ? editing.deliveryInvoiceId.trim() : null,
      watermarkBeforePayment: editing.watermarkBeforePayment,
    };
    setSaving(true);
    try {
      await api.admin.updateDocument(editing.doc.id, editing.doc.ownerId, body);
      setEditing(null);
      await loadDocs();
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await api.admin.deleteDocument(deleteTarget.id, deleteTarget.ownerId, deleteTarget.signed);
      setDeleteTarget(null);
      await loadDocs();
    } catch (e) {
      setError(apiErrorMessage(e));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  const upd = (patch: Partial<EditState>): void => setEditing((s) => (s ? { ...s, ...patch } : s));

  if (loading) return <div className="p-6 text-sm text-[var(--color-muted)]">Loading…</div>;

  const previewable = (d: DocumentWithLabel): boolean =>
    d.contentType === 'application/pdf' || d.contentType.startsWith('image/');

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text)]">Documents</h1>
      </div>

      <Card className="relative z-10">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Scope" tip="Where documents live. Internal documents are never client-visible. Client and project documents stay hidden from the client too, until you grant per-file access below.">
            <select
              className={`${inputClass} md:min-w-[180px]`}
              value={scopeKind}
              onChange={(e) => {
                setScopeKind(e.target.value as ScopeKind);
                setProjectId('');
              }}
            >
              <option value="all">All documents</option>
              <option value="business-admin">Internal Documents</option>
              <option value="client">Client</option>
              <option value="project">Project</option>
            </select>
          </Field>
          {scopeKind === 'business-admin' ? (
            <Field label="Business" required>
              <select className={`${inputClass} md:min-w-[220px]`} value={businessId} onChange={(e) => setBusinessId(e.target.value)}>
                <option value="">Pick a business…</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </Field>
          ) : null}
          {scopeKind === 'client' || scopeKind === 'project' ? (
            <Field label="Client" required>
              <select
                className={`${inputClass} md:min-w-[220px]`}
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  setProjectId('');
                }}
              >
                <option value="">Pick a client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.displayName}</option>
                ))}
              </select>
            </Field>
          ) : null}
          {scopeKind === 'project' ? (
            <Field label="Project" required>
              <select className={`${inputClass} md:min-w-[220px]`} value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!clientId}>
                <option value="">{clientId ? 'Pick a project…' : 'Pick a client first'}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
          ) : null}
          {folders.length > 0 ? (
            <Field label="Folder" tip="Folders are labels on files, not containers, so an empty folder cannot exist; a folder disappears when its last file loses the label. To rename one, select its files and bulk-set the new name.">
              <select className={`${inputClass} md:min-w-[180px]`} value={folderFilter} onChange={(e) => { setFolderFilter(e.target.value); setSelected(new Set()); }}>
                <option value="">All folders</option>
                <option value="__none__">No folder</option>
                {folders.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
      </Card>

      {scope ? (
        <Card className="relative z-20">
          <div className="space-y-3 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Type" tip="Applied to each file in this upload. Editable per file afterward.">
                <input className={inputClass} list="doc-type-suggestions" value={uploadDocType} onChange={(e) => setUploadDocType(e.target.value)} maxLength={60} placeholder="e.g. agreement" />
                <datalist id="doc-type-suggestions">
                  {DOC_TYPE_SUGGESTIONS.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </Field>
              <Field label="Folder" tip="Optional grouping label applied to each file in this upload, e.g. a project phase or a service name.">
                <input className={inputClass} value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)} maxLength={100} placeholder="optional" />
              </Field>
            </div>
            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
              <div className="mb-2 flex items-center gap-1 text-sm font-medium text-[var(--color-text)]">
                Client access for this upload
                <InfoTip text="Applied to every file in this upload; editable per file afterward. Default is DENY. 'Deliver on payment' keeps the file hidden until its invoice is paid in full (Stage 4). 'Watermark before payment' serves a brand-watermarked copy instead of the original for images and PDFs. Non-previewable types (Office, ZIP, text) cannot be watermarked, so under either protection they stay fully locked until payment." />
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={uploadView} onChange={(e) => setUploadView(e.target.checked)} />
                  Client can view
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={uploadDownload} onChange={(e) => setUploadDownload(e.target.checked)} />
                  Client can download
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={uploadOnPayment} onChange={(e) => setUploadOnPayment(e.target.checked)} />
                  Deliver on payment
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={uploadWatermark || uploadWatermarkForced} disabled={uploadWatermarkForced} onChange={(e) => setUploadWatermark(e.target.checked)} />
                  Watermark before payment
                  {uploadWatermarkForced ? <span className="text-xs text-[var(--color-muted)]">(required with deliver-on-payment)</span> : null}
                </label>
              </div>
              {(uploadOnPayment || uploadWatermark || uploadWatermarkForced) && clientId ? (
                <div className="mt-3">
                  <Field label="Unlock on payment of" tip="Pick the invoice whose full payment unlocks the clean file for the uploaded file(s). Without a linked invoice a deliver-on-payment file never delivers, and a watermarked file stays permanently watermarked. You can also set or change this per file later by editing it.">
                    {uploadInvoices === null ? (
                      <div className="px-3 py-2 text-sm text-[var(--color-muted)]">Loading this client's invoices…</div>
                    ) : uploadInvoices.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[var(--color-muted)]">No invoices for this client yet. Create one under Invoices (or link it per file later by editing).</div>
                    ) : (
                      <select className={inputClass} value={uploadInvoiceId} onChange={(e) => setUploadInvoiceId(e.target.value)}>
                        <option value="">Not linked (deliver-on-payment stays locked; watermark is permanent)</option>
                        {uploadInvoices.map((inv) => (
                          <option key={inv.id} value={inv.id}>{invoiceOptionLabel(inv)}</option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => fileInput.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading…' : 'Upload files'}
              </Button>
              <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => void doUpload(e.target.files)} />
              <p className="text-xs text-[var(--color-muted)]">
                PDF, images, Office files, ZIP, or text. 25 MB per file.
              </p>
            </div>
          </div>
        </Card>
      ) : scopeKind !== 'all' ? (
        <p className="text-sm text-[var(--color-muted)]">Pick a scope above to view and upload documents.</p>
      ) : null}

      {error ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}

      {selected.size > 0 ? (
        <Card className="relative z-30">
          <div className="flex flex-wrap items-end gap-3 p-4">
            <span className="pb-2 text-sm font-medium text-[var(--color-text)]">{selected.size} selected</span>
            <Field label="Viewable">
              <select className={inputClass} value={bulk.clientViewable} onChange={(e) => setBulk({ ...bulk, clientViewable: e.target.value as TriChoice })}>
                <option value="">No change</option>
                <option value="on">Grant</option>
                <option value="off">Revoke</option>
              </select>
            </Field>
            <Field label="Downloadable">
              <select className={inputClass} value={bulk.clientDownloadable} onChange={(e) => setBulk({ ...bulk, clientDownloadable: e.target.value as TriChoice })}>
                <option value="">No change</option>
                <option value="on">Grant</option>
                <option value="off">Revoke</option>
              </select>
            </Field>
            {/* Linking an invoice draws attention to the two protection triggers (owner rule):
                the admin should consciously set the right client access alongside the link. */}
            <Field label="On payment">
              <select className={`${inputClass}${bulkInvoiceAttention}`} value={bulk.deliveryOnPayment} onChange={(e) => setBulk({ ...bulk, deliveryOnPayment: e.target.value as TriChoice })}>
                <option value="">No change</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </Field>
            <Field label="Watermark">
              <select className={`${inputClass}${bulkInvoiceAttention}`} value={bulk.watermarkBeforePayment} onChange={(e) => setBulk({ ...bulk, watermarkBeforePayment: e.target.value as TriChoice })}>
                <option value="">No change</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </Field>
            <Field label="Invoice" tip="Link every selected document to the invoice whose full payment unlocks it. Offered when the whole selection belongs to one client. Remember to set On payment or Watermark so the link actually protects the files.">
              <select
                className={inputClass}
                value={bulk.invoiceMode === 'clear' ? 'clear' : bulk.invoiceMode === 'set' ? bulk.invoiceId || 'set' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setBulk({ ...bulk, invoiceMode: '', invoiceId: '' });
                  else if (v === 'clear') setBulk({ ...bulk, invoiceMode: 'clear', invoiceId: '' });
                  else setBulk({ ...bulk, invoiceMode: 'set', invoiceId: v === 'set' ? '' : v });
                }}
              >
                <option value="">No change</option>
                {bulkClientId
                  ? allInvoices.filter((i) => i.clientId === bulkClientId).map((i) => (
                      <option key={i.id} value={i.id}>{invoiceOptionLabel(i)}</option>
                    ))
                  : null}
                <option value="clear">Clear link</option>
              </select>
            </Field>
            <Field label="Folder">
              <select className={inputClass} value={bulk.folderMode} onChange={(e) => setBulk({ ...bulk, folderMode: e.target.value as BulkState['folderMode'] })}>
                <option value="">No change</option>
                <option value="set">Set to…</option>
                <option value="clear">Clear folder</option>
              </select>
            </Field>
            {bulk.folderMode === 'set' ? (
              <Field label="Folder name" required>
                <input className={inputClass} value={bulk.folder} onChange={(e) => setBulk({ ...bulk, folder: e.target.value })} maxLength={100} />
              </Field>
            ) : null}
            <div className="pb-1">
              <Button onClick={() => void applyBulk()} disabled={bulkBusy}>{bulkBusy ? 'Applying…' : 'Apply to selected'}</Button>
            </div>
          </div>
          {/* Rendered OUTSIDE the items-end flex row on purpose: inside the Invoice Field this
              paragraph added height below that column's select, and bottom-alignment turned that
              into the whole Invoice column riding up relative to every other control. */}
          {!bulkClientId ? (
            <p className="px-4 pb-3 text-xs text-[var(--color-muted)]">Linking needs a one-client selection; clearing works on any.</p>
          ) : null}
        </Card>
      ) : null}

      {editing ? (
        <Card className="relative z-40">
          <div className="space-y-3 p-4">
            <h2 className="text-base font-semibold text-[var(--color-text)]">Edit document: {editing.doc.fileName}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Title" required>
                <input className={inputClass} value={editing.title} onChange={(e) => upd({ title: e.target.value })} maxLength={200} />
              </Field>
              <Field label="Type" required>
                <input className={inputClass} list="doc-type-suggestions" value={editing.docType} onChange={(e) => upd({ docType: e.target.value })} maxLength={60} />
              </Field>
              <Field label="Folder">
                <input className={inputClass} value={editing.folder} onChange={(e) => upd({ folder: e.target.value })} maxLength={100} placeholder="blank = none" />
              </Field>
              <Field label="Issued date">
                <input type="date" className={inputClass} value={editing.issuedDate} onChange={(e) => upd({ issuedDate: e.target.value })} />
              </Field>
            </div>

            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
              <div className="mb-2 flex items-center gap-1 text-sm font-medium text-[var(--color-text)]">
                Client access
                <InfoTip text="Default is DENY: with nothing checked the client never sees this file. 'Deliver on payment' keeps the clean file locked until its designated invoice is paid in full (Stage 4); then the client is emailed a link and gets the clean file. 'Watermark before payment' serves a brand-watermarked copy instead of the original for images and PDFs, with no email, and after payment the clean file replaces it. Deliver-on-payment together with view or download forces watermark on for previewable types. Non-previewable types (Office, ZIP, text) can't be watermarked, so under either protection they stay fully locked until payment." />
              </div>
              {(() => {
                const wmForced = editing.deliveryOnPayment && (editing.clientViewable || editing.clientDownloadable) && previewable(editing.doc);
                const hardLocked = !previewable(editing.doc) && (editing.deliveryOnPayment || editing.watermarkBeforePayment);
                return (
                  <>
                    <div className="grid gap-2 md:grid-cols-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editing.clientViewable} onChange={(e) => upd({ clientViewable: e.target.checked })} />
                        Client can view
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editing.clientDownloadable} onChange={(e) => upd({ clientDownloadable: e.target.checked })} />
                        Client can download
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editing.deliveryOnPayment} onChange={(e) => upd({ deliveryOnPayment: e.target.checked })} />
                        Deliver on payment
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={editing.watermarkBeforePayment || wmForced} disabled={wmForced} onChange={(e) => upd({ watermarkBeforePayment: e.target.checked })} />
                        Watermark before payment
                        {wmForced ? <span className="text-xs text-[var(--color-muted)]">(required)</span> : null}
                      </label>
                    </div>
                    {hardLocked ? (
                      <p className="mt-2 text-xs text-[var(--tint-warn-text)]">
                        This file type can't be watermarked, so while protected it stays fully locked to the client until payment.
                      </p>
                    ) : null}
                    {(editing.deliveryOnPayment || editing.watermarkBeforePayment) && editing.doc.ownerType !== 'business-admin' ? (
                      <div className="mt-2 md:w-2/3">
                        <Field
                          label="Unlock on payment of"
                          tip="Pick the invoice whose full payment unlocks the clean file. Invoices are created under Invoices in the nav; issued ones show their number (e.g. AIT-INV-2026-0002), drafts show as 'Draft'. On full payment the watermark lifts / the file unlocks, and for 'Deliver on payment' the client is also emailed a link. Leave unlinked for a permanent watermark, or a deliver-on-payment file that stays locked."
                        >
                          {editInvoices === null ? (
                            <div className="px-3 py-2 text-sm text-[var(--color-muted)]">Loading this client's invoices…</div>
                          ) : editInvoices.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-[var(--color-muted)]">
                              No invoices for this client yet. Create one under Invoices, then link it here.
                            </div>
                          ) : (
                            <select className={inputClass} value={editing.deliveryInvoiceId} onChange={(e) => upd({ deliveryInvoiceId: e.target.value })}>
                              <option value="">Not linked (stays locked / permanently watermarked)</option>
                              {editInvoices.map((inv) => (
                                <option key={inv.id} value={inv.id}>{invoiceOptionLabel(inv)}</option>
                              ))}
                            </select>
                          )}
                        </Field>
                      </div>
                    ) : null}
                    {(() => {
                      // Warn when a protection trigger is on but nothing valid is linked. Deliver-on-
                      // payment with no invoice never delivers (a real mistake). Watermark with no
                      // invoice is a permanent watermark (a valid choice, §18.2a) — note it softly.
                      if (editing.doc.ownerType === 'business-admin' || editInvoices === null) return null;
                      const linkedValid = editInvoices.some((i) => i.id === editing.deliveryInvoiceId);
                      if (linkedValid) return null;
                      if (editing.deliveryOnPayment) {
                        return (
                          <p className="mt-2 text-xs font-medium text-[var(--tint-red-text)]">
                            "Deliver on payment" is on but no invoice is linked, so this file will never unlock or deliver to the client. Pick an invoice above.
                          </p>
                        );
                      }
                      if (editing.watermarkBeforePayment) {
                        return (
                          <p className="mt-2 text-xs text-[var(--tint-warn-text)]">
                            No invoice is linked, so this file stays permanently watermarked (it never unlocks). Link an invoice above if it should unlock on payment.
                          </p>
                        );
                      }
                      return null;
                    })()}
                  </>
                );
              })()}
            </div>

            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
              <div className="mb-2 flex items-center gap-1 text-sm font-medium text-[var(--color-text)]">
                Signing lifecycle
                <InfoTip text="Marked by you at this stage (e-signing is out of scope). Deleting a signed document later requires a strong confirmation." />
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.signed} onChange={(e) => upd({ signed: e.target.checked })} />
                  Signed
                </label>
                <Field label="Signed date">
                  <input type="date" className={inputClass} value={editing.signedDate} onChange={(e) => upd({ signedDate: e.target.value })} disabled={!editing.signed} />
                </Field>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.agreedUpon} onChange={(e) => upd({ agreedUpon: e.target.checked })} />
                  Agreed upon
                </label>
              </div>
            </div>

            {formError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{formError}</div> : null}
            <div className="flex gap-2">
              <Button onClick={() => void doSave()} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            </div>
          </div>
        </Card>
      ) : null}

      {listLoading ? (
        <p className="text-sm text-[var(--color-muted)]">Loading documents…</p>
      ) : visibleDocs.length === 0 && (scope || scopeKind === 'all') ? (
        <p className="text-sm text-[var(--color-muted)]">
          {docs.length > 0 ? 'No documents match this folder filter.' : `No documents${scope ? ' in this scope yet. Upload some above.' : ' yet.'}`}
        </p>
      ) : (
        <div className="space-y-2">
          {visibleDocs.map((d) => (
            <Card key={d.id}>
              <div className="flex flex-wrap items-start gap-3 p-4">
                <input type="checkbox" className="mt-1" checked={selected.has(d.id)} onChange={() => toggleSelected(d.id)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-[var(--color-text)]">{d.title}</span>
                    <span className="rounded-full bg-[var(--tint-slate-bg)] px-2 py-0.5 text-xs text-[var(--tint-slate-text)]">{OWNER_TYPE_LABEL[d.ownerType]}: {d.ownerLabel}</span>
                    {d.folder ? <span className="rounded-full bg-[var(--tint-blue-bg-soft)] px-2 py-0.5 text-xs text-[var(--tint-blue-text)]">Folder: {d.folder}</span> : null}
                    {d.signed ? <span className="rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs text-[var(--tint-green-text)]">Signed{d.signedDate ? ` ${displayDate(d.signedDate)}` : ''}</span> : null}
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-muted)]">
                    {d.fileName} · {d.docType} · {formatBytes(d.sizeBytes)} · uploaded {displayDate(d.createdAt)}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {d.clientViewable ? <span className="rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs text-[var(--tint-green-text)]">View</span> : null}
                    {d.clientDownloadable ? <span className="rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs text-[var(--tint-green-text)]">Download</span> : null}
                    {d.deliveryOnPayment ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs ${d.paid ? 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]' : 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]'}`}>
                        {d.paid ? 'Paid' : 'On payment'}
                      </span>
                    ) : null}
                    {d.watermarkBeforePayment ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs ${d.paid ? 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]' : 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]'}`}>
                        {d.paid ? 'Paid' : 'Watermark'}
                      </span>
                    ) : null}
                    {d.deliveryInvoiceId ? (
                      <span className={`rounded-full px-2 py-0.5 text-xs ${d.paid ? 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]' : 'bg-[var(--tint-blue-bg-soft)] text-[var(--tint-blue-text)]'}`}>
                        Unlocks on {invoiceById.get(d.deliveryInvoiceId)?.documentNumber ?? 'a draft invoice'}{d.paid ? ' (paid)' : ''}
                      </span>
                    ) : d.deliveryOnPayment || d.watermarkBeforePayment ? (
                      <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs text-[var(--tint-warn-text)]">No invoice linked</span>
                    ) : null}
                    {!d.clientViewable && !d.clientDownloadable && !d.deliveryOnPayment && !d.watermarkBeforePayment && !d.uploadedByClient ? (
                      <span className="rounded-full bg-[var(--tint-red-bg)] px-2 py-0.5 text-xs text-[var(--tint-red-text)]">Hidden from client</span>
                    ) : null}
                    {d.uploadedByClient && !d.clientViewable && !d.clientDownloadable && !d.deliveryOnPayment && !d.watermarkBeforePayment ? (
                      <span className="rounded-full bg-[var(--tint-slate-bg)] px-2 py-0.5 text-xs text-[var(--tint-slate-text)]">Client upload</span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <a
                    className={pillClasses()}
                    href={api.admin.documentFileUrl(d.id, d.ownerId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open
                  </a>
                  <a
                    className={pillClasses()}
                    href={api.admin.documentFileUrl(d.id, d.ownerId, true)}
                  >
                    Download
                  </a>
                  <PillButton onClick={() => { setFormError(null); setEditing(editFromDoc(d)); }}>Edit</PillButton>
                  <PillButton tone="red" onClick={() => setDeleteTarget(d)}>Delete</PillButton>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title={deleteTarget.signed ? 'Delete a SIGNED document?' : 'Delete document?'}
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          {deleteTarget.signed ? (
            <p>
              "{deleteTarget.title}" is marked SIGNED. Deleting it permanently destroys a signed record and its file.
              This cannot be undone. Are you certain?
            </p>
          ) : (
            <p>Delete "{deleteTarget.title}" and its file? This cannot be undone.</p>
          )}
        </ConfirmDialog>
      ) : null}

      {bulkConfirm ? (
        <ConfirmDialog
          open={true}
          title="Relink documents to a different invoice?"
          confirmLabel="Relink"
          confirmVariant="danger"
          busy={bulkBusy}
          onConfirm={() => void submitBulk(bulkConfirm.set, bulkConfirm.items, true)}
          onCancel={() => setBulkConfirm(null)}
        >
          <div className="space-y-2 text-left">
            <p className="text-sm">
              {bulkConfirm.conflicts.length === 1
                ? 'One selected document is already linked to a different invoice. Applying will replace that link:'
                : `${bulkConfirm.conflicts.length} selected documents are already linked to a different invoice. Applying will replace those links:`}
            </p>
            <ul className="max-h-48 space-y-1 overflow-y-auto text-xs text-[var(--color-muted)]">
              {bulkConfirm.conflicts.map((c, i) => (
                <li key={i}>
                  <span className="font-medium text-[var(--color-text)]">"{c.title}"</span>: currently unlocks on {c.currentLabel}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[var(--color-muted)]">
              A relinked document unlocks on the NEW invoice's full payment instead. Documents already on the
              chosen invoice (or with no link) are unaffected either way.
            </p>
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
